import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';
import * as moment from 'moment';

import { ILogbookService } from '@services/data';
import {
    ComponentDefinition,
    Group,
    IApprovalButtonsConfiguration,
    ManualInputLogbook,
    NameBuilderConfiguration,
    TagManualInputValues,
    WidgetValues,
} from '../../models';

class ManualInputsTableController implements angular.IComponentController {
    daysOff: number[] = [];
    focuses: { [formControlName: string]: boolean } = {};
    hasApprovalColumn: boolean = false;
    indexDaysOff: number[];
    indexWeekBeginDay: number;
    inputValuesForm: angular.IFormController;
    style: { [cssProperty: string]: string } = {};

    readonly alwaysEdit: boolean;
    readonly applicationTimezone: string;
    readonly approvalConfig: IApprovalButtonsConfiguration;
    readonly boundData: WidgetValues;
    readonly dateKeyList: string[];
    readonly dates: Date[];
    readonly valueFieldWidth?: number;
    readonly group: Group;
    readonly hasManualInputRole: boolean;
    readonly inputList: TagManualInputValues[];
    readonly isApprovable: { [date: string]: boolean };
    readonly isApprovalAvailable: boolean;
    readonly isVertical: boolean;
    readonly nameConfiguration: NameBuilderConfiguration;
    readonly showLogbook: boolean;
    readonly save: () => void;
    readonly validate?: () => void;
    readonly weekBeginDay: number;

    private _hasConsecutiveDays: boolean;

    static $inject = ['logbookService'];

    constructor(private _logbookService: ILogbookService) {
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['isApprovable'] && changes['isApprovable'].currentValue) {
            const isApprovable = changes['isApprovable'].currentValue;
            this.hasApprovalColumn = this.isAtLeastOneDateApprovable(isApprovable);
        }

        if (changes['dates'] && this.dates) {
            const daysOfDates = this.dates.map(d => d.getDay());
            this.indexWeekBeginDay = daysOfDates.indexOf(this.weekBeginDay);
            this._hasConsecutiveDays = this.isConsecutiveDays();
            this.updateDaysOff();
        }
        if (this.inputValuesForm && this.alwaysEdit && !this.inputValuesForm.$visible) {
            this.inputValuesForm.$show();
        }

        if (this.inputList?.length > 0 && this.valueFieldWidth) {
            this.inputList.forEach(input => {
                input.classed = { 'fixed-width': true };
                input.style = {
                    'width': this.valueFieldWidth + 1 + 'em',
                    'min-width': this.valueFieldWidth + 1 + 'em'
                };
            });

            let valueFieldWidth = this.valueFieldWidth;
            if (this.inputList.filter(x => !!x.digitalSet).length > 0) {
                valueFieldWidth += 1;
            }
            this.style = {
                'width': valueFieldWidth + 'em',
                'min-width': valueFieldWidth + 'em'
            };
        }
    }

    onBeforeSave() {
        if (this.validate) {
            this.validate();
        }
    }

    clickCell(input: TagManualInputValues, value: ManualInputLogbook, $event: angular.IAngularEvent, edit: boolean = false): void {
        $event.stopPropagation();
        const guid = input.identifierVm?.guid ?? input.guid;
        this._logbookService.openModal(
            guid,
            value.badge,
            edit,
            this.hasManualInputRole,
            this.showLogbook,
            value.range,
            undefined,
            this.group as any
        );
    }

    isValid(input: TagManualInputValues): boolean {
        const result = input.values.filter(v => v.value !== undefined && v.value !== null).length > 0;
        return result;
    }

    areAllValuesSet(inputList: TagManualInputValues[]): boolean {
        return _.every(inputList, input => _.every(input.values, value => value.hasData));
    }

    isAtLeastOneDateApprovable(isApprovable: { [date: string]: boolean }): boolean {
        if (isApprovable) {
            const values = _.values(isApprovable);
            return _.some(values, value => value === true);
        }
        return false;
    }

    onFocus(controlName: string) {
        this.focuses[controlName] = true;
    }

    onBlur(controlName: string) {
        this.focuses[controlName] = false;
    }

    private isConsecutiveDays(): boolean {
        let result = false;

        if (this.dates && this.dates.length > 1) {
            // convert all dates to app timezone
            const dates = this.dates.map(d => moment(d).tz(this.applicationTimezone));
            // check each date is preceding the previous one from one day
            const pairs = d3.pairs(dates);
            result = _.every(pairs, d => d[0].clone().add(1, 'd').valueOf() === d[1].valueOf());
        }

        return result;
    }

    private updateDaysOff() {
        if (this.weekBeginDay || this.weekBeginDay === 0) {
            this.daysOff = this.computeDayOff(this.weekBeginDay);
            this.updateIndexDaysOff();
        }
    }

    private updateIndexDaysOff() {
        if (this.dates && this.daysOff) {
            this.indexDaysOff = this.dates
                .map((d, i) => { return { date: d, index: i }; })
                .filter(d => this.isDayOff(d.date))
                .map(d => d.index);
        }
    }

    private isDayOff(d: Date): boolean {
        let isDayOff = false;
        if (this._hasConsecutiveDays) {
            isDayOff = this.daysOff.indexOf(d.getDay()) >= 0;
        }
        return isDayOff;
    }

    private computeDayOff(weekBeginDay: number): number[] {
        let daysOff = [];
        if (this._hasConsecutiveDays) {
            daysOff = [(weekBeginDay - 2 + 7) % 7, (weekBeginDay - 1 + 7) % 7];
        }
        return daysOff;
    }
}

export const wtManualInputsTable: ComponentDefinition = {
    componentId: 'wtManualInputsTable',
    controller: ManualInputsTableController,
    template: require('./manualInputsTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        alwaysEdit: '<?',
        applicationTimezone: '<',
        approvalConfig: '<',
        boundData: '<?',
        dateFormat: '<?',
        dateKeyList: '<?',
        dates: '<',
        decimals: '<?',
        group: '<?',
        hasManualInputRole: '<?',
        inputList: '<',
        inputValuesForm: '=?',
        isApprovable: '<',
        isApprovalAvailable: '<?',
        isApprovalDisabled: '<?',
        isApproved:'<?',
        isVertical: '<?',
        nameConfiguration: '<?',
        pickerPosition: '<?',
        save: '&',
        showLogbook: '<?',
        showMissingWarnings: '<?',
        validate: '&?',
        valueFieldWidth: '<?',
        weekBeginDay: '<'
    }
};